// import { Spin } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useWatch, useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import Content from './Content';
import { FilterRevenueDTO } from './filter-revenue.dto';
import Header from './Header';

import { DownloadIcon } from '@/assets/icons';
import LayoutContent from '@/layout/Content';
import revenueService from '@/services/revenue.service';
import { TypeRevenueDownload } from '@/types/product-store';
import { handleMessageError } from '@/util/handleMessageError';
import { handleNotification } from '@/util/handleNotification';
import { exportCSVFile } from '@/util/jsonToCsv';

const Revenue = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const methods = useForm<FilterRevenueDTO>();

    const { control } = methods;

    const [params, setParams] = useState<any>({
        storeId: null,
        date: null,
        redSlip: false,
    });
    const {
        isLoading,
        data: revenue,
        refetch,
    } = useQuery(['revenue', params.storeId], () => revenueService.getRevenueReport(params), {
        enabled: !!params.storeId || !!params.date,
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    const { mutate } = useMutation((body: TypeRevenueDownload) => revenueService.getRevenueReportDownload(body), {
        onSuccess: (data: any) => {
            exportCSVFile({ data, fileName: t('REVENUE') });
        },
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    const onHandleFilter = (data: FilterRevenueDTO) => {
        setParams({
            storeId: data.storeId,
            date: data.date,
            redSlip: data.redSlip,
        });
    };

    const onHandleClearData = () => {
        queryClient.removeQueries('revenue');
        setParams({ storeId: null, date: null, redSlip: false });
    };

    const formMonitor = useWatch({
        control,
    });

    const exportCsv = () => {
        // if (!formMonitor || !formMonitor?.franchiseId) {
        //     return handleNotification(t('VALIDATION.REQUIRED_SELECT', { name: t('FC_NAME') }), 'error', t);
        // }
        if (!formMonitor?.storeId) {
            return handleNotification(t('VALIDATION.REQUIRED_SELECT', { name: t('STORE_NAME') }), 'error', t);
        }

        if (!formMonitor?.date) {
            return handleNotification(t('VALIDATION.REQUIRED_FILL', { name: t('TARGET_DATE') }), 'error', t);
        }

        const body = {
            storeId: formMonitor?.storeId,
            date: formMonitor?.date && moment(formMonitor?.date).format('YYYY-MM-DD'),
            redSlip: formMonitor?.redSlip,
        };
        mutate(body);
    };

    useEffect(() => {
        if (params.storeId && params.date) {
            refetch();
        }
    }, [params, refetch]);

    return (
        <LayoutContent
            className="p-revenue"
            title={`${t('SALES_TOTAL')} / ${t('REVENUE')}`}
            actionFooter={[
                {
                    url: '/',
                    title: `${t('EXPORT_CSV')}`,
                    icon: <DownloadIcon />,
                    exportCsv,
                },
            ]}
        >
            <FormProvider {...methods}>
                <Header onClearData={onHandleClearData} onHandleData={onHandleFilter} />
                <Content data={revenue?.data} total={revenue?.total} isLoading={isLoading} />
            </FormProvider>
        </LayoutContent>
    );
};

export default Revenue;
